'use client';

import Image from 'next/image';
import { useEffect, useRef, useState, useCallback, type MouseEvent } from 'react';
import { openTrackedWhatsAppHref } from '@/lib/whatsapp/open-tracked-whatsapp';
import WhatsAppIntakeSheet, {
  type WhatsAppIntakeContext,
} from '@/components/whatsapp/WhatsAppIntakeSheet';
import WhatsAppGlyph from '@/components/whatsapp/WhatsAppGlyph';

interface BuildingHeroProps {
  imageUrl: string;
  imageAlt: string;
  title: string;
  subtitle: string;
  ctaPrimaryText: string;
  ctaPrimaryHref: string;
  ctaSecondaryText?: string;
  ctaSecondaryHref?: string;
  /** Si viene, el CTA secundario abre el asistente de fechas (W2) en vez de wa.me. */
  intake?: Omit<WhatsAppIntakeContext, 'ctaPosition'>;
  /** Si se pasa, muestra "★ X / 5 · N reseñas verificadas" sobre el título (above-fold trust signal) */
  ratingBadge?: { ratingValue: number; reviewCount: number };
  /**
   * Sources de video para el hero. Cuando están presentes, en desktop
   * (≥768px) y SIN `prefers-reduced-motion`, se renderiza `<video>` autoplay
   * loop muted en lugar del `<Image>` estático. Mobile solo renderiza video
   * si se pasa `mobileVideoSources`; reduced-motion y data-saver siempre ven poster.
   */
  videoSources?: { webm?: string; mp4: string };
  /** Source mobile opcional. Debe ser liviano (<900 KB ideal) y seguro para crop vertical. */
  mobileVideoSources?: { webm?: string; mp4: string };
  /** Poster preferido (AVIF/WebP) para el video. Si no se pasa, usa imageUrl. */
  posterUrl?: string;
}

/**
 * Hero con parallax sutil + entry stagger animation. Server-safe fallback:
 * sin JS o con prefers-reduced-motion, todo el contenido es visible inmediatamente
 * (opacity 1 por defecto). Solo cuando el cliente confirma que puede animar,
 * activa la entrada y el parallax.
 *
 * Modo video desktop:
 *   - Si videoSources presentes + viewport ≥768px + sin prefers-reduced-motion
 *     → render <video> autoplay muted loop playsInline en lugar de <Image>.
 *   - Parallax se DESACTIVA cuando hay video (el video ya provee movimiento).
 *   - Mobile (<768px) solo usa video si recibe `mobileVideoSources`.
 *   - prefers-reduced-motion y data-saver siempre ven poster estático.
 *
 * Por qué Client Component: parallax requiere scroll listener y el feature-detect
 * de viewport/reduced-motion solo es posible en el cliente.
 */
export default function BuildingHero({
  imageUrl,
  imageAlt,
  title,
  subtitle,
  ctaPrimaryText,
  ctaPrimaryHref,
  ctaSecondaryText,
  ctaSecondaryHref,
  intake,
  ratingBadge,
  videoSources,
  mobileVideoSources,
  posterUrl,
}: BuildingHeroProps) {
  const mediaRef = useRef<HTMLDivElement | null>(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [activeVideoSources, setActiveVideoSources] = useState<
    { webm?: string; mp4: string } | null
  >(null);
  const useVideo = Boolean(activeVideoSources);

  // Tracking custom: cta_click_whatsapp con position=building_hero. Autocapture
  // funciona aquí (es <a>), pero el evento custom permite atribución por posición.
  const onSecondaryClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!ctaSecondaryHref) return;
      event.preventDefault();
      openTrackedWhatsAppHref(ctaSecondaryHref, { cta_position: 'building_hero' });
    },
    [ctaSecondaryHref]
  );

  // Decide si renderizar <video>. Desktop usa `videoSources`; mobile solo usa
  // `mobileVideoSources`. En data-saver o reduced-motion no carga video.
  useEffect(() => {
    const mqDesktop = window.matchMedia('(min-width: 768px)');
    const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean };
    }).connection;

    const update = () => {
      if (mqReduced.matches || connection?.saveData) {
        setActiveVideoSources(null);
        return;
      }

      if (mqDesktop.matches && videoSources?.mp4) {
        setActiveVideoSources(videoSources);
        return;
      }

      if (!mqDesktop.matches && mobileVideoSources?.mp4) {
        setActiveVideoSources(mobileVideoSources);
        return;
      }

      setActiveVideoSources(null);
    };

    // Perf (2026-06-18): NO activar el video de inmediato. El poster (priority,
    // AVIF) es el elemento LCP; descargar el video en paralelo le roba ancho de
    // banda en mobile 4G y empeora LCP/Speed Index. Diferimos la primera
    // activación hasta que la página terminó de cargar + el navegador está idle,
    // así el poster pinta primero y el video entra después sin competir.
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let scheduledId: number | null = null;
    const schedule = () => {
      if (typeof win.requestIdleCallback === 'function') {
        scheduledId = win.requestIdleCallback(update, { timeout: 2500 });
      } else {
        scheduledId = window.setTimeout(update, 1200);
      }
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    mqDesktop.addEventListener('change', update);
    mqReduced.addEventListener('change', update);
    return () => {
      window.removeEventListener('load', schedule);
      if (scheduledId !== null) {
        if (typeof win.cancelIdleCallback === 'function') win.cancelIdleCallback(scheduledId);
        else window.clearTimeout(scheduledId);
      }
      mqDesktop.removeEventListener('change', update);
      mqReduced.removeEventListener('change', update);
    };
  }, [videoSources, mobileVideoSources]);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    // Trigger entry animation (next frame, garantiza que la transición se vea).
    // Aplica con o sin video — los textos/CTA siempre tienen entry stagger.
    requestAnimationFrame(() => setAnimateIn(true));

    // Parallax SOLO si NO hay video activo. Cuando el video reproduce su push-in,
    // sumar parallax JS encima genera mareo (regla validada Gemini+Grok 2026-05-05).
    if (useVideo) return;

    const node = mediaRef.current;
    const section = node?.closest('section');
    if (!node || !section) return;

    let ticking = false;
    let listenerAttached = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const offset = window.scrollY * 0.3;
        node.style.transform = `translate3d(0, ${offset}px, 0)`;
        ticking = false;
      });
    };

    const attachListener = () => {
      if (listenerAttached) return;
      listenerAttached = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    };
    const detachListener = () => {
      if (!listenerAttached) return;
      listenerAttached = false;
      window.removeEventListener('scroll', onScroll);
    };

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) attachListener();
        else detachListener();
      },
      { rootMargin: '20% 0px 50% 0px' }
    );
    visibilityObserver.observe(section);

    return () => {
      visibilityObserver.disconnect();
      detachListener();
    };
  }, [useVideo]);

  // El estado inicial (opacity 0 + translate) se aplica SOLO via CSS:
  // `.pa-hero[data-animate-state='pending'] .pa-hero__*` en app/globals.css.
  // El inline style aporta únicamente `transition` con su delay individual,
  // sin sobreescribir opacity/transform (eso rompía la transición — los inline
  // styles ganan especificidad sobre cualquier CSS rule).
  const staggerTransition = (delayMs: number): React.CSSProperties => ({
    transition: `opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms, transform 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms`,
  });

  const effectivePoster = posterUrl ?? imageUrl;

  return (
    <section
      data-animate-state={animateIn ? 'in' : 'pending'}
      className="pa-hero relative w-full h-[74svh] min-h-[600px] max-h-[680px] md:h-[70vh] md:min-h-[640px] md:max-h-[720px] overflow-hidden"
    >
      <div
        ref={mediaRef}
        className="pa-hero__media absolute inset-0"
        // Background con el poster: SIEMPRE presente (no solo cuando hay video).
        // Es la primera pintura (LCP) por CSS, sin depender de hidratación ni de
        // next/image; garantiza que nunca se vea un rectángulo gris/negro mientras
        // el video (diferido) o el <Image> cargan. Cubre también el caso
        // poster-only (reduced-motion, data-saver, Safari iOS sin gesture).
        style={{
          backgroundImage: `url(${effectivePoster})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {activeVideoSources?.mp4 ? (
          <video
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={effectivePoster}
            aria-hidden="true"
            disablePictureInPicture
          >
            {activeVideoSources.webm && (
              <source src={activeVideoSources.webm} type="video/webm" />
            )}
            <source src={activeVideoSources.mp4} type="video/mp4" />
          </video>
        ) : (
          <Image
            src={effectivePoster}
            alt={imageAlt}
            fill
            priority
            fetchPriority="high"
            sizes="100vw"
            className="object-cover scale-110"
          />
        )}
      </div>
      {/* Overlay en dos capas: una sutil arriba que respeta la calidad del video
          (badge ahora es glass oscuro con fondo propio + el header ya tiene su gradient
          from-black/35 to-transparent encima), y un degradé reforzado abajo donde está
          el copy h1/subtitle/CTAs. Centro casi limpio para mostrar el edificio con calidad.
          Iteración previa from-black/45 via-black/35 to-black/75 oscurecía demasiado el
          video; ahora el text-shadow del título + el bottom darken bastan para WCAG. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/5 to-black/65 pointer-events-none" />
      {/* pt-20 (80px) = buffer mínimo bajo el header transparente (h≈60px) para
          que el badge "reseñas verificadas" nunca se solape con el logo cuando
          el contenido del hero (badge + h1 + subtitle + CTAs ≈ 430px) excede
          la altura disponible. Combinado con min-h-[600px] mobile / [640px]
          desktop, el contenido completo cabe sin desbordarse hacia arriba a
          zoom 100-200% en cualquier breakpoint. El justify-end mantiene la
          alineación al fondo en viewports altos. */}
      <div className="relative h-full container mx-auto px-4 flex flex-col justify-end pt-20 pb-16">
        {/* max-w-3xl mantiene la línea legible (60-70 chars) en desktop wide.
            Antes era max-w-4xl: textos cruzaban toda la pantalla en 1920 → muro ilegible. */}
        <div className="max-w-3xl">
          {ratingBadge && ratingBadge.reviewCount > 0 && (
            <div
              className="pa-hero__badge inline-flex items-center gap-2 mb-3 rounded-full bg-black/35 backdrop-blur-md border border-white/15 px-3 py-1.5 text-sm font-semibold text-white shadow-lg shadow-black/20"
              style={staggerTransition(0)}
            >
              <span aria-hidden="true" className="text-amber-400">
                ★
              </span>
              <span>
                {ratingBadge.ratingValue.toLocaleString('es-CO', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                / 5
              </span>
              <span aria-hidden="true" className="text-white/40">
                ·
              </span>
              <span className="font-normal text-white/85">
                {ratingBadge.reviewCount.toLocaleString('es-CO')}
              </span>
              {/* En mobile mostramos solo "reseñas" para mantener el badge corto
                  y elegante. En sm+ aparece "verificadas" para reforzar trust. */}
              <span className="font-normal text-white/85">
                reseñas<span className="hidden sm:inline"> en Airbnb y Booking</span>
              </span>
            </div>
          )}
          <h1
            className="pa-hero__title text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight [text-shadow:_0_2px_14px_rgba(0,0,0,0.5)]"
            style={staggerTransition(120)}
          >
            {title}
          </h1>
          <p
            className="pa-hero__subtitle text-lg md:text-xl text-white/90 mb-7 max-w-2xl [text-shadow:_0_1px_8px_rgba(0,0,0,0.4)]"
            style={staggerTransition(260)}
          >
            {subtitle}
          </p>
          <div className="pa-hero__ctas flex flex-wrap gap-3" style={staggerTransition(400)}>
            {/* Iter 2026-05-19: tamaño reducido (px-5 py-2.5 + text-sm md:text-base)
                para que la pareja de CTAs quede proporcional al logo del header y
                al h1; antes (px-6 py-3) los botones se sentían grandes vs el resto
                del sitio. */}
            <a
              href={ctaPrimaryHref}
              className="inline-flex items-center justify-center bg-white text-gray-900 hover:-translate-y-px transition-transform font-semibold text-sm md:text-base px-5 py-2.5 rounded-xl shadow-sm"
            >
              {ctaPrimaryText}
            </a>
            {ctaSecondaryText && intake ? (
              // Asistente de fechas (W2): el CTA abre el intake calificado;
              // el cta_click_whatsapp lo emite el sheet al salir.
              <button
                type="button"
                onClick={() => setIntakeOpen(true)}
                data-cta-position="building_hero"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/40 hover:-translate-y-px transition-all text-white font-semibold text-sm md:text-base px-5 py-2.5 rounded-xl"
              >
                <WhatsAppGlyph size={18} />
                <span>{ctaSecondaryText}</span>
              </button>
            ) : ctaSecondaryText && ctaSecondaryHref && (
              <a
                href={ctaSecondaryHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onSecondaryClick}
                data-cta-position="building_hero"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/40 hover:-translate-y-px transition-all text-white font-semibold text-sm md:text-base px-5 py-2.5 rounded-xl"
              >
                {/* Brief 2026-05-18 P0: icono WhatsApp + texto corto "WhatsApp".
                    Antes "Hablar por WhatsApp" sin icono peleaba con el primario
                    por longitud — ahora queda visualmente más sobrio y reconocible. */}
                <WhatsAppGlyph size={18} />
                <span>{ctaSecondaryText}</span>
              </a>
            )}
          </div>
        </div>
      </div>
      {/* Scroll hint — micro-indicador sutil para invitar al scroll inicial */}
      <svg
        aria-hidden="true"
        className="pa-hero__scroll-hint text-white/85"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
      {/* Gradient mask inferior — suaviza la transición Hero → TrustBar.
          Evita el corte recto entre el bloque oscuro y el contenido claro. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/35 to-transparent"
      />
      {intake && (
        <WhatsAppIntakeSheet
          open={intakeOpen}
          onClose={() => setIntakeOpen(false)}
          context={{ ...intake, ctaPosition: 'intake_hero' }}
        />
      )}
    </section>
  );
}
